import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {IdName, LoaderService, ResponsiveService, ToastService} from '@core/src/public-api';
import {MapsComponent} from '@shared/components/maps/maps.component';
import {appointmentVoucherIdentificationType, appointmentInsuranceIdentificationType} from '@shared/constants/appointment.constants';
import {AppointmentArrival} from '@shared/models/appointment.model';
import {AppointmentService} from '@shared/services/appointment.service';
import {MapsService} from '@shared/services/maps.service';

@Component({
  selector: 'si-appo-arrival-confirm-dialog',
  templateUrl: './appo-arrival-confirm-dialog.component.html',
  styleUrls: ['./appo-arrival-confirm-dialog.component.scss']
})
export class AppoArrivalConfirmDialogComponent implements OnInit {
  @ViewChild('maps') maps: MapsComponent;
  @Input() appoId: number;
  @Input() displayDialog: boolean;
  @Input() centerAddress: string;
  @Input() centerLatitude: number;
  @Input() centerLongitude: number;
  @Input() centerName: string;
  @Input() cardNumberSEOGA: string;
  @Input() actions: string[];
  @Output() displayDialogChange = new EventEmitter<boolean>();
  @Output() appoArrivalConfirmed = new EventEmitter<any>();
  @Output() appoArrivalError = new EventEmitter<any>();
  model = new AppointmentArrival();
  isDesktop: boolean;
  isTablet: boolean;
  isMapsInitialized = false;
  mapOptions;
  markerOptions;
  latitude: number;
  longitude: number;
  mapHeight: number;
  appointmentInsuranceIdentificationType = appointmentInsuranceIdentificationType;
  appointmentVoucherIdentificationType = appointmentVoucherIdentificationType;
  allowVouchers: boolean;
  requiresSEOGA: boolean;
  requiresSEOGAAuthorization: boolean;
  insuranceCardNumber: string;
  insuranceLocator: string;
  voucherId: string;
  voucherList: IdName[];
  displayConfirmArrivalErrorDialog: boolean;

  constructor(
    private loaderService: LoaderService,
    private mapsService: MapsService,
    private responsiveService: ResponsiveService,
    private appointmentService: AppointmentService,
    private toastService: ToastService
  ) {
    this.setResponsiveLogic();
  }

  ngOnInit(): void {
    this.initVouchersAndMaps();
    if (this.isDesktop) {
      this.mapHeight = 300;
    } else if (this.isTablet) {
      this.mapHeight = 200;
    } else {
      this.mapHeight = 150;
    }

    this.insuranceCardNumber = this.cardNumberSEOGA;
    this.model.insuranceIdentificationCode = this.cardNumberSEOGA;
  }

  setResponsiveLogic(): void {
    this.isDesktop = this.responsiveService.isDesktop();
    this.isTablet = this.responsiveService.isTablet();
  }

  async initVouchersAndMaps(): Promise<void> {
    await this.checkVoucherManagement();
    setTimeout(() => {
      this.initMapsLibrary();
    });
  }

  async initMapsLibrary(): Promise<void> {
    this.loaderService.start();
    try {
      await this.mapsService.initLibrary();
      this.mapOptions = {
        disableDefaultUI: true,
        zoomControl: false,
        keyboardShortcuts: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        clickableIcons: false
      };
      this.markerOptions = {animation: google.maps.Animation.DROP};
      this.isMapsInitialized = true;
    } catch (e) {
    } finally {
      this.loaderService.stop();
    }
  }

  async checkVoucherManagement(): Promise<void> {
    if (this.actions?.includes('IS_SEOGA_REQUIRED') || !this.actions?.includes('IS_VOUCHERS_ALLOWED')) {
      return;
    }

    await this.getAppointmentVouchers();
  }

  async getAppointmentVouchers(): Promise<void> {
    this.displayDialog = false;
    this.loaderService.start();
    try {
      this.voucherList = await this.appointmentService.getAppointmentVouchers(this.appoId);
      if (this.voucherList?.length) {
        this.allowVouchers = true;
      }
    } finally {
      this.displayDialog = true;
      this.loaderService.stop();
    }
  }

  onClickConfirmArrival(): void {
    this.setAppointmentConfirmArrival();
  }

  async setAppointmentConfirmArrival(): Promise<void> {
    try {
      await this.appointmentService.setAppointmentConfirmArrival(this.appoId, this.model);
      await this.toastService.success('SI_CONFIRM_ARRIVAL_SUCCESS');
      this.appoArrivalConfirmed.emit();
      this.onClickCloseDialog();
    } catch (e) {
      switch (e.error?.applicationCode) {
        case 'PCS0046':
          this.requiresSEOGA = true;
          this.requiresSEOGAAuthorization = true;
          this.model.insuranceIdentificationCode = e.error?.message;
          break;
        case 'PCS0047':
          this.requiresSEOGA = true;
          this.model.insuranceIdentificationCode = e.error?.message;
          break;
        case 'PCS0045':
        default:
          this.manageAppointmentArrivalError();
      }

      throw e;
    }
  }

  manageAppointmentArrivalError(): void {
    this.displayDialog = false;
    this.displayConfirmArrivalErrorDialog = true;
    this.appoArrivalError.emit();
  }

  onChangeInsuranceIdentificationType(): void {
    switch (this.model.insuranceIdentificationType) {
      case appointmentInsuranceIdentificationType.CARD_NUMBER:
        this.model.insuranceIdentificationCode = this.insuranceCardNumber;
        break;
      case appointmentInsuranceIdentificationType.LOCATOR:
        this.model.insuranceIdentificationCode = this.insuranceLocator;
        break;
      default:
    }
  }

  onChangeInsuranceIdentificationCode(): void {
    switch (this.model.insuranceIdentificationType) {
      case appointmentInsuranceIdentificationType.CARD_NUMBER:
        this.insuranceCardNumber = this.model.insuranceIdentificationCode;
        break;
      case appointmentInsuranceIdentificationType.LOCATOR:
        this.insuranceLocator = this.model.insuranceIdentificationCode;
        break;
      default:
    }
  }

  onChangeVoucherIdentificationType(): void {
    if (this.model.voucherIdentificationType === appointmentVoucherIdentificationType.VOUCHER) {
      this.model.voucherId = this.voucherId;
    } else {
      this.model.voucherId = null;
    }
  }

  onChangeVoucherId(): void {
    this.voucherId = this.model.voucherId;
  }

  onClickCloseDialog(): void {
    this.displayDialog = false;
    this.displayDialogChange.emit(this.displayDialog);
  }

  onCloseConfirmArrivalErrorDialog(): void {
    this.onClickCloseDialog();
  }
}
